import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import {
  DollarSign, Users, CheckCircle, XCircle, TrendingUp, Calculator, FileText,
  Eye, Plus, Filter, Download, Search, ArrowRight, ArrowDown, ArrowLeft,
  Zap, Trash2, Loader2, Calendar, Upload, AlertCircle, Settings, Check,
  RefreshCw, Car, LogOut, Lock, Mail, Send, UserCheck, ChevronLeft
} from "lucide-react";
import { API, axios, formatIcelandicNumber, calculatePenaltyInterest, getLoanTypeLabel } from "@/utils/constants";
import { generatePaymentSchedule } from "@/utils/paymentSchedule";
import { useAuth } from "@/context/AuthContext";

// Næsti Mánuður (Next 12 Months Income) Component
export const NaestiManudurDetails = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [monthlyIncome, setMonthlyIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ vextir: 17.7, greidslus: 250 });

  useEffect(() => {
    calculateMonthlyIncome();
  }, []);

  const calculateMonthlyIncome = async () => {
    try {
      // Fetch all loans
      const loansResponse = await axios.get(`${API}/loans`);
      const loans = loansResponse.data;
      
      // Fetch settings
      const settingsResponse = await axios.get(`${API}/settings`);
      setSettings(settingsResponse.data);
      
      // Calculate income for next 12 months based on actual scheduled payments
      const today = new Date();
      const monthlyData = [];
      
      for (let i = 0; i < 12; i++) {
        const targetDate = new Date(today.getFullYear(), today.getMonth() + i + 1, 1);
        const monthName = targetDate.toLocaleDateString('is-IS', { year: 'numeric', month: 'long' });
        const targetYear = targetDate.getFullYear();
        const targetMonth = targetDate.getMonth() + 1; // 1-12
        
        let totalIncome = 0;
        let paymentCount = 0;
        
        // Check each active loan for payments in this specific month
        loans.forEach(loan => {
          if (loan.status === 'approved' || loan.status === 'disbursed') {
            // Check if loan has payment_schedule
            if (loan.payment_schedule && Array.isArray(loan.payment_schedule)) {
              // Look through the payment schedule for this loan
              loan.payment_schedule.forEach(payment => {
                // Parse the date (format: "DD.MM.YYYY")
                let paymentDate;
                if (payment.date) {
                  const dateParts = payment.date.split('.');
                  if (dateParts.length === 3) {
                    // Create date from DD.MM.YYYY
                    paymentDate = new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
                  }
                }
                
                if (paymentDate) {
                  const paymentYear = paymentDate.getFullYear();
                  const paymentMonth = paymentDate.getMonth() + 1;
                  
                  // If payment is in the target month, add to total
                  if (paymentYear === targetYear && paymentMonth === targetMonth) {
                    const monthlyPayment = payment.payment || 0;
                    const greidslus = loan.greidslus || settingsResponse.data.greidslus || 250;
                    
                    totalIncome += monthlyPayment + greidslus;
                    paymentCount++;
                  }
                }
              });
            }
          }
        });
        
        monthlyData.push({
          month: monthName,
          monthNumber: i + 1,
          totalIncome: Math.round(totalIncome),
          paymentCount: paymentCount
        });
      }
      
      setMonthlyIncome(monthlyData);
      setLoading(false);
    } catch (error) {
      console.error('Error calculating monthly income:', error);
      toast.error('Ekki tókst að reikna mánaðarlegar tekjur');
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8">Hleð mánaðarlegar tekjur...</div>;
  }

  const totalYearIncome = monthlyIncome.reduce((sum, month) => sum + month.totalIncome, 0);
  const averageMonthlyIncome = Math.round(totalYearIncome / 12);

  return (
    <div className="p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          {!embedded && (
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
              size="sm" className="h-7 text-xs px-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Til baka
            </Button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Næstu 12 Mánuðir - Tekjur</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">Meðaltal á mánuði</p>
              <p className="text-xl font-bold text-blue-600">{formatIcelandicNumber(averageMonthlyIncome)} kr.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">Samtals 12 mánuði</p>
              <p className="text-xl font-bold text-green-600">{formatIcelandicNumber(totalYearIncome)} kr.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-gray-600">Greiðslur í mánuði 1</p>
              <p className="text-xl font-bold text-gray-900">{monthlyIncome[0]?.paymentCount || 0}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-4">
            <h2 className="text-lg font-bold mb-3">Mánaðarlegar tekjur</h2>
            <div className="space-y-1.5">
              {monthlyIncome.map((month, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-semibold text-sm">{month.monthNumber}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900">{month.month}</p>
                      <p className="text-xs text-gray-500">{month.paymentCount} greiðslur</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">{formatIcelandicNumber(month.totalIncome)} kr.</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


